import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Plug } from 'lucide-react';
import { toast } from 'sonner';
import { useDecideApproval } from '../../../hooks/mutations';
import { goSignIn } from '../BotConnections';
import { CardRow, CardShell, ResolvedCard, dangerBtn, ghostBtn, inputCls, outcomeOf, primaryBtn } from './CardShell';

const WAITS_FOR_SIGN_IN = ['pending', 'needs_sign_in', 'needs_client'];

/** A key the server needs, typed here and never shown to the bot; it can be shown while typing. */
const SecretField = ({ secret, value, onChange }) => {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  return (
    <label className="block">
      <span className="block text-xs text-muted-foreground mb-1">
        <span className="font-mono text-foreground">{secret.name}</span>
        {secret.hint && <span className="ml-1.5">{secret.hint}</span>}
      </span>
      <span className="flex items-center gap-1.5">
        {secret.prefix && <span className="shrink-0 font-mono text-xs text-muted-foreground">{secret.prefix.trim()}</span>}
        <span className="relative min-w-0 flex-1">
          <input value={value} type={shown ? 'text' : 'password'} autoComplete="off" maxLength={4000}
            onChange={(e) => onChange(e.target.value)} data-testid="app-card-secret"
            className={`${inputCls} pr-8 font-mono text-xs`} />
          <button type="button" onClick={() => setShown((v) => !v)}
            aria-label={t(shown ? 'bots.connections.hide_value' : 'bots.connections.show_value')}
            title={t(shown ? 'bots.connections.hide_value' : 'bots.connections.show_value')}
            className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground">
            {shown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        </span>
      </span>
    </label>
  );
};

/**
 * The bot adds, changes or disconnects one of its apps or MCP servers (agents/cards.py app_card).
 * The person sees where it connects and who uses it, types the keys it needs (they go straight
 * into the connection, encrypted: the bot only knows their names) and confirms or says no.
 */
const McpServerCard = ({ approval, botName, compact }) => {
  const { t } = useTranslation();
  const decide = useDecideApproval();
  const card = approval.card;
  const action = card.action;
  const secrets = card.secrets || [];
  const changes = card.changes || {};
  const [values, setValues] = useState({});

  if (approval.status !== 'pending') {
    const applied = approval.applied || {};
    const failed = !!applied.error && applied.status !== 'starting';
    const waiting = WAITS_FOR_SIGN_IN.includes(applied.status);
    const done = failed ? t('bots.cards.failed')
      : waiting ? t('bots.connections.status_needs_sign_in') : t(`bots.cards.app_done_${action}`);
    return (
      <ResolvedCard icon={Plug} testid="app-card-resolved" text={card.name}
        ok={approval.status === 'approved' && !failed} outcome={outcomeOf(approval, t, done)} />
    );
  }

  const ready = secrets.every((s) => (values[s.name] || '').trim());
  const send = (approve) => decide.mutate({
    approvalId: approval.id, approve, edits: approve && secrets.length ? { secrets: values } : undefined,
  }, {
    onSuccess: (res) => { if (res?.applied?.authorize_url) goSignIn(res.applied.authorize_url); },
    onError: (err) => toast.error(err?.response?.data?.detail || t('bots.cards.failed')),
  });
  const command = card.command && [card.command, ...(card.args || [])].join(' ');
  const shownCommand = changes.command ? [changes.command, ...(changes.args || [])].join(' ') : command;
  const settings = Object.entries(changes.settings || card.settings || {});
  const rules = Object.entries(changes.tool_rules || {});
  const confirmCls = action === 'remove' ? dangerBtn : primaryBtn;

  return (
    <CardShell icon={Plug} testid="app-card" waiting compact={compact}
      eyebrow={t(`bots.cards.app_eyebrow_${action}`, { bot: botName })} title={card.name}
      footer={(
        <>
          <span className="flex-1" />
          <button type="button" className={ghostBtn} onClick={() => send(false)} disabled={decide.isPending}
            data-testid="card-decline">
            {t('bots.cards.not_now')}
          </button>
          <button type="button" className={confirmCls} onClick={() => send(true)}
            disabled={decide.isPending || !ready} data-testid="card-confirm">
            {t(`bots.cards.app_confirm_${action}`)}
          </button>
        </>
      )}>
      <div className="pb-1">
        {card.reason && <p className="text-[13px] text-foreground/85 mb-1.5">{card.reason}</p>}
        {changes.name && (
          <CardRow label={t('bots.cards.bot_name')}>
            <span><span className="line-through text-muted-foreground mr-1.5">{card.name}</span>{changes.name}</span>
          </CardRow>
        )}
        {card.url && <CardRow label={t('bots.connections.kind_remote')}><span className="break-all">{card.url}</span></CardRow>}
        {shownCommand && (
          <CardRow label={t('bots.connections.kind_program')}>
            <span className="font-mono text-xs break-all">{shownCommand}</span>
          </CardRow>
        )}
        {action === 'add' && (
          <CardRow label={t('bots.cards.who')}>
            {t(card.sharing === 'personal' ? 'bots.connections.share_personal' : 'bots.connections.share_team')}
            {card.project_name && <span className="text-muted-foreground"> · {t('bots.connections.only_here', { project: card.project_name })}</span>}
          </CardRow>
        )}
        {settings.length > 0 && (
          <CardRow label={t('bots.cards.app_settings')}>
            {settings.map(([k, v]) => <span key={k} className="block font-mono text-xs break-all">{k}={v}</span>)}
          </CardRow>
        )}
        {rules.length > 0 && (
          <CardRow label={t('bots.cards.app_tool_rules')}>
            {rules.map(([tool, rule]) => (
              <span key={tool} className="block"><span className="font-mono text-xs">{tool}</span>: {t(`bots.connections.policy_${rule}`)}</span>
            ))}
          </CardRow>
        )}
        {(changes.remove_secrets || []).length > 0 && (
          <CardRow label={t('bots.cards.app_removed_keys')}>
            <span className="font-mono text-xs">{changes.remove_secrets.join(', ')}</span>
          </CardRow>
        )}
        {secrets.length > 0 && (
          <div className="mt-2 space-y-2">
            {secrets.map((s) => (
              <SecretField key={s.name} secret={s} value={values[s.name] || ''}
                onChange={(v) => setValues((cur) => ({ ...cur, [s.name]: v }))} />
            ))}
            <p className="text-[11px] text-muted-foreground">{t('bots.cards.app_keys_hint', { bot: botName })}</p>
          </div>
        )}
      </div>
    </CardShell>
  );
};

export default McpServerCard;
